import { authFetch } from './api.js';

/** Owns the image-detail modal and keeps image-group state outside the view controller. */
export function createImageManagerDetailController({
  getImage,
  parseMetadata,
  formatOwnerLabel,
  scheduleRefresh,
  selectById = (id) => document.getElementById(id),
  documentRef = document,
  now = () => Date.now(),
} = {}) {
  function closePreview() {
    documentRef.querySelector('.im-image-lightbox')?.remove();
  }

  function show(listingIndex, imageIndex) {
    const image = getImage?.(listingIndex, imageIndex);
    if (!image) return;

    const meta = parseMetadata(image.metadata);
    const overlay = selectById('im-detail-overlay');
    if (!overlay) return;

    const detailImage = selectById('im-detail-img');
    if (detailImage) detailImage.src = `/api/image/assets/${image.id}`;
    const title = selectById('im-detail-title');
    if (title) title.textContent = meta.slotNumber ? `Slot ${meta.slotNumber} 图片` : '图片详情';
    const slotType = selectById('im-detail-slot-type');
    if (slotType) slotType.textContent = meta.slotType || '';
    const variant = selectById('im-detail-variant');
    if (variant) variant.textContent = meta.variant || '';
    const provider = selectById('im-detail-provider');
    if (provider) provider.textContent = meta.provider ? `provider: ${meta.provider}` : '';
    const model = selectById('im-detail-model');
    if (model) model.textContent = meta.model ? `model: ${meta.model}` : '';
    const size = selectById('im-detail-size');
    if (size) size.textContent = image.file_size ? `${(image.file_size / 1024).toFixed(0)} KB` : '';
    const owner = selectById('im-detail-owner');
    if (owner) {
      owner.textContent = formatOwnerLabel({
        owner_user_name: image.owner_user_name,
        owner_user_email: image.owner_user_email,
        user_id: image.user_id,
      }) || '—';
    }
    const prompt = selectById('im-detail-prompt');
    if (prompt) prompt.value = meta.prompt || image.prompt || '';
    const regenerateStatus = selectById('im-detail-regen-status');
    if (regenerateStatus) {
      regenerateStatus.hidden = true;
      delete regenerateStatus.dataset?.state;
    }

    overlay.hidden = false;
    overlay._currentImageId = image.id;
    overlay._currentListingIdx = listingIndex;
    overlay._currentImageIdx = imageIndex;
  }

  function close() {
    closePreview();
    const overlay = selectById('im-detail-overlay');
    if (overlay) overlay.hidden = true;
  }

  function openPreview() {
    const image = selectById('im-detail-img');
    const src = image?.currentSrc || image?.src || '';
    if (!src) return;
    closePreview();

    const overlay = documentRef.createElement('div');
    overlay.className = 'im-image-lightbox';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', '查看图片');

    const fullImage = documentRef.createElement('img');
    fullImage.className = 'im-image-lightbox__image';
    fullImage.src = src;
    fullImage.alt = image?.alt || '图片预览';

    const closeButton = documentRef.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'im-image-lightbox__close';
    closeButton.textContent = '×';
    closeButton.setAttribute('aria-label', '关闭图片预览');

    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) closePreview();
    });
    closeButton.addEventListener('click', closePreview);
    overlay.append(fullImage, closeButton);
    documentRef.body.appendChild(overlay);
    closeButton.focus?.();
  }

  async function regenerate() {
    const overlay = selectById('im-detail-overlay');
    const assetId = overlay?._currentImageId;
    if (!assetId) return;
    const prompt = selectById('im-detail-prompt');
    const newPrompt = prompt?.value.trim() || '';
    const button = selectById('im-btn-regenerate');
    const status = selectById('im-detail-regen-status');
    if (!button || !status) return;

    button.disabled = true;
    button.textContent = '生成中...';
    setRegenerateStatus(status, '正在重新生成(基于原图+编辑指令,使用紫鸟 imageEditV2)...', 'pending');

    try {
      const payload = { assetId };
      if (newPrompt) payload.newPrompt = newPrompt;
      const response = await authFetch('/api/image/regenerate-v2', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error);
      const assets = Array.isArray(data.assets) ? data.assets : [];
      if (!assets.length) throw new Error('重新生成未返回图片');

      const firstAsset = assets[0];
      const detailImage = selectById('im-detail-img');
      if (detailImage) detailImage.src = firstAsset.url || `/api/image/assets/${firstAsset.id}`;
      overlay._currentImageId = firstAsset.id;
      setRegenerateStatus(status, `重新生成完成,新增 ${assets.length} 张图(已追加到当前项目)`, 'success');
      scheduleRefresh?.();
    } catch (error) {
      setRegenerateStatus(status, `生成失败: ${error.message}`, 'error');
    } finally {
      button.disabled = false;
      button.textContent = '重新生成';
    }
  }

  function download() {
    const overlay = selectById('im-detail-overlay');
    const assetId = overlay?._currentImageId;
    const detailImage = selectById('im-detail-img');
    const source = assetId ? `/api/image/assets/${assetId}?download=1` : detailImage?.src;
    if (!source) return;
    const link = documentRef.createElement('a');
    link.href = source;
    link.download = `image-${now()}.png`;
    documentRef.body.appendChild(link);
    link.click();
    link.remove();
  }

  return Object.freeze({ show, close, openPreview, closePreview, regenerate, download });
}

function setRegenerateStatus(status, message, state) {
  status.hidden = false;
  status.textContent = message;
  status.dataset.state = state;
}
